import React, { memo, useCallback, useEffect, useMemo, useState, forwardRef, useImperativeHandle } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { BUY_SELL_MAIN, CFD, DEFAULT_TRADE_AP_TERM_ID } from 'shared-modules/constants';
import { BUILDER_ORDER_TYPES } from 'shared-modules/constants/builder';
import classNames from 'classnames';
import { useTradeAPLogic } from 'shared-modules/services/hooks/tradeAPLogic';
import {
  useBuilderFinalPrice,
  useBuilderPricePrecision,
  useCheckOnNegativePriceWhenSimulate,
  useGetBuilderOrderRequestData,
} from 'shared-modules/services/hooks/builder';
import { getPipsLabelWithParentheses } from 'shared-modules/utils';
import { TRADE_AP_DESCRIPTION_LINK } from 'shared-modules/config';
import styles from './sideMenuOptionsTradeAP.module.scss';
import { InputNumber, BuySellGroupButton, HelpHalfModal, Switch, Button } from '../../../../components';
import infoImagesHelper from '../../../../assets/builderInfo/infoImagesHelper';
import {
  getBuilderSimulationDataRequest,
  changeBuilderOrderEditStatus,
  resetBuilderOptions,
  resetBuilderOrdersTables,
  resetBuilderSimulationData,
  changeBuilderOrderType,
} from '../../../../redux/actions';
import { ReactComponent as NewsModeIcon } from '../../../../assets/newsMode.svg';

const shortNumberInputWidth = 78;

const SideMenuOptionsTradeAP = forwardRef((props, ref) => {
  const dispatch = useDispatch();
  useEffect(() => {
    const orderType = BUILDER_ORDER_TYPES.MULTI.ID;
    const resetAll = () => {
      dispatch(resetBuilderOptions());
      dispatch(resetBuilderOrdersTables());
      dispatch(resetBuilderSimulationData());
    };

    // constructor reset
    resetAll();
    dispatch(changeBuilderOrderType({ orderType }));

    // destructor reset
    return resetAll;
  }, [dispatch]);
  const serviceId = useSelector((state) => state.auth.serviceId);
  const instrumentId = useSelector((state) => state.builder.activeCurrency);
  const [detailSettingIsOpen, setDetailSettingIsOpen] = useState(false);
  const [isFocusInputRange, setIsFocusInputRange] = useState(false);
  const submitCallback = useCallback(
    (succeeded) => {
      if (succeeded) {
        dispatch(changeBuilderOrderEditStatus({ allowOrderEdit: false }));
      }
    },
    [dispatch],
  );
  const multiFormLogic = useTradeAPLogic(submitCallback, true);

  useImperativeHandle(
    ref,
    () => {
      return {
        submit: multiFormLogic.submit,
      };
    },
    [multiFormLogic],
  );

  const { selectedSellBuyId } = useSelector((state) => state.builder.multiOrder);
  const isBuy = useMemo(() => Number(selectedSellBuyId) === BUY_SELL_MAIN.BUY.ID, [selectedSellBuyId]);
  const { pricePrecision } = useBuilderPricePrecision();
  const finalPrice = useBuilderFinalPrice();

  const pipsLabelWithParentheses = getPipsLabelWithParentheses(serviceId, instrumentId);

  const handleExplanation = () => {
    window.open(TRADE_AP_DESCRIPTION_LINK);
  };

  const formatPriceValue = useCallback(
    (value) => {
      if (value == null || value === '') return '';
      const num = Number(value);
      return Number.isNaN(num) ? '' : num.toFixed(pricePrecision);
    },
    [pricePrecision],
  );

  const toggleDetailSetting = () => {
    setDetailSettingIsOpen((prev) => !prev);
    multiFormLogic.viewedDetailSettings.set(true);
  };

  const getOrderRequestData = useGetBuilderOrderRequestData();
  const checkOnNegativePrice = useCheckOnNegativePriceWhenSimulate();
  const getSimulationData = useCallback(() => {
    const successCallback = () => {
      const orderRequestData = getOrderRequestData();
      dispatch(
        getBuilderSimulationDataRequest({
          orderRequestData,
          termId: DEFAULT_TRADE_AP_TERM_ID,
        }),
      );
    };

    checkOnNegativePrice(successCallback);
  }, [checkOnNegativePrice, dispatch, getOrderRequestData]);

  const addedLogicGroups = useSelector((state) => state.builder.logicGroupsList);
  const hasLogicGroups = useMemo(() => addedLogicGroups.length > 0, [addedLogicGroups.length]);
  const allowOrderEdit = useSelector((state) => state.builder.allowOrderEdit);
  const getSimulationDataTrigger = hasLogicGroups && !allowOrderEdit;

  useEffect(() => {
    if (getSimulationDataTrigger) {
      getSimulationData();
    }
  }, [getSimulationData, getSimulationDataTrigger]);

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <div className={styles.settings}>
          <div className={styles.descriptionButtonContainer}>
            <Button onClick={handleExplanation} className={styles.descriptionButton} outlined>
              <NewsModeIcon className={styles.articleIcon} />
              <span className={styles.descriptionButtonText}>解説</span>
            </Button>
          </div>
          <div className={styles.item}>
            <div className={styles.label}>
              {multiFormLogic.buySell.label}
              <HelpHalfModal
                className={styles.help}
                content={multiFormLogic.buySell.tooltipMessage}
                logicName={multiFormLogic.buySell.label}
                logicImageSrc={infoImagesHelper[multiFormLogic.buySell.descriptionImage]}
              />
            </div>
            <BuySellGroupButton
              buySell={multiFormLogic.buySell.get}
              onChange={multiFormLogic.buySell.set}
              disabledSell={multiFormLogic.buySell.isSellDisabled}
              disabledBuy={multiFormLogic.buySell.isBuyDisabled}
              isNotSelectable={multiFormLogic.buySell.isNotSelectable || !allowOrderEdit}
            />
          </div>
          <div className={classNames(styles.item, styles.itemPaddingTop)}>
            <div className={styles.label}>
              {multiFormLogic.entryPricePipsValue.label}
              <HelpHalfModal
                className={styles.help}
                content={multiFormLogic.entryPricePipsValue.tooltipMessage}
                logicName={multiFormLogic.entryPricePipsValue.label}
                logicImageSrc={infoImagesHelper[multiFormLogic.entryPricePipsValue.descriptionImage]}
              />
            </div>
            {isBuy ? (
              <InputNumber
                onFocus={() => setIsFocusInputRange(true)}
                onBlur={() => setIsFocusInputRange(false)}
                value={
                  isFocusInputRange
                    ? multiFormLogic.entryPriceInput.get
                    : formatPriceValue(multiFormLogic.entryPriceInput.get)
                }
                onChange={multiFormLogic.entryPriceInput.set}
                disabled={multiFormLogic.entryPriceInput.isDisabled || !allowOrderEdit}
                step={multiFormLogic.entryPriceInput.step}
                min={multiFormLogic.entryPricePipsValue.min}
                name={multiFormLogic.entryPriceInput.name}
                errorMessages={multiFormLogic.errorMessages}
                withErrorTooltip
              />
            ) : (
              <div className={styles.readOnlyContainer}>
                <div className={styles.input}>{finalPrice}</div>
              </div>
            )}
          </div>
          <div className={styles.itemRangeIconWrapper}>
            <div className={styles.itemRangeIcon}>〜</div>
          </div>
          <div className={classNames(styles.item, styles.right, styles.itemPaddingBottom)}>
            {!isBuy ? (
              <InputNumber
                onFocus={() => setIsFocusInputRange(true)}
                onBlur={() => setIsFocusInputRange(false)}
                value={
                  isFocusInputRange
                    ? multiFormLogic.entryPriceInput.get
                    : formatPriceValue(multiFormLogic.entryPriceInput.get)
                }
                onChange={multiFormLogic.entryPriceInput.set}
                disabled={multiFormLogic.entryPriceInput.isDisabled || !allowOrderEdit}
                step={multiFormLogic.entryPriceInput.step}
                min={multiFormLogic.entryPricePipsValue.min}
                name={multiFormLogic.entryPriceInput.name}
                errorMessages={multiFormLogic.errorMessages}
                withErrorTooltip
              />
            ) : (
              <div className={styles.readOnlyContainer}>
                <div className={styles.input}>{finalPrice}</div>
              </div>
            )}
          </div>

          <div className={styles.item}>
            <div className={styles.label}>
              {multiFormLogic.rangeSpread.label + pipsLabelWithParentheses}
              <HelpHalfModal
                className={styles.help}
                content={multiFormLogic.rangeSpread.tooltipMessage}
                logicName={multiFormLogic.rangeSpread.label}
                logicImageSrc={infoImagesHelper[multiFormLogic.rangeSpread.descriptionImage]}
              />
            </div>
            <InputNumber
              value={multiFormLogic.rangeSpread.get}
              onChange={multiFormLogic.rangeSpread.set}
              disabled={multiFormLogic.rangeSpread.isDisabled || !allowOrderEdit}
              step={multiFormLogic.rangeSpread.step}
              name={multiFormLogic.rangeSpread.name}
              errorMessages={multiFormLogic.errorMessages}
              withErrorTooltip
            />
          </div>
          <div className={styles.item}>
            <div className={styles.label}>
              {multiFormLogic.itemsCount.label}
              <HelpHalfModal
                className={styles.help}
                content={multiFormLogic.itemsCount.tooltipMessage}
                logicName={multiFormLogic.itemsCount.label}
                logicImageSrc={infoImagesHelper[multiFormLogic.itemsCount.descriptionImage]}
              />
            </div>
            <InputNumber
              value={multiFormLogic.itemsCount.get}
              onChange={multiFormLogic.itemsCount.set}
              disabled={multiFormLogic.itemsCount.isDisabled || !allowOrderEdit}
              step={multiFormLogic.itemsCount.step}
              name={multiFormLogic.itemsCount.name}
              errorMessages={multiFormLogic.errorMessages}
              withErrorTooltip
              onlyIntegerAllowed
            />
          </div>
          <div className={styles.item}>
            <div className={styles.label}>
              <div className={styles.twoLinesLabel}>
                <div>{multiFormLogic.quantity.label}</div>
                {serviceId === CFD && <div className={styles.unit}>{multiFormLogic.quantityUnitConvFactor.label}</div>}
              </div>
              <HelpHalfModal
                className={styles.help}
                content={multiFormLogic.quantity.tooltipMessage}
                logicName={multiFormLogic.quantity.label}
                logicImageSrc={infoImagesHelper[multiFormLogic.quantity.descriptionImage]}
              />
            </div>
            <InputNumber
              value={multiFormLogic.quantity.get}
              onChange={multiFormLogic.quantity.set}
              disabled={multiFormLogic.quantity.isDisabled || !allowOrderEdit}
              step={multiFormLogic.quantity.step}
              name={multiFormLogic.quantity.name}
              errorMessages={multiFormLogic.errorMessages}
              withErrorTooltip
            />
          </div>
          <div className={styles.item}>
            <div className={styles.label}>
              {multiFormLogic.profitMargin.label + pipsLabelWithParentheses}
              <HelpHalfModal
                className={styles.help}
                content={multiFormLogic.profitMargin.tooltipMessage}
                logicName={multiFormLogic.profitMargin.label}
                logicImageSrc={infoImagesHelper[multiFormLogic.profitMargin.descriptionImage]}
              />
            </div>
            <InputNumber
              value={multiFormLogic.profitMargin.get}
              onChange={multiFormLogic.profitMargin.set}
              disabled={multiFormLogic.profitMargin.isDisabled || !allowOrderEdit}
              step={multiFormLogic.profitMargin.step}
              name={multiFormLogic.profitMargin.name}
              errorMessages={multiFormLogic.errorMessages}
              withErrorTooltip
            />
          </div>
          <div
            className={classNames(styles.item, styles.detailSetting)}
            onClick={toggleDetailSetting}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                toggleDetailSetting();
              }
            }}
          >
            詳細設定
            <i
              className={classNames(
                'material-icons-outlined',
                styles.infoIcon,
                detailSettingIsOpen ? null : styles.iconRotateDown,
              )}
            >
              keyboard_arrow_up
            </i>
          </div>
        </div>
        {detailSettingIsOpen ? (
          <div>
            <div className={styles.title}>詳細設定</div>
            <div className={styles.settings}>
              <div className={styles.item}>
                <div className={styles.label}>
                  {multiFormLogic.ocoCheckbox.label}
                  <HelpHalfModal
                    className={styles.help}
                    content={multiFormLogic.ocoCheckbox.tooltipMessage}
                    logicName={multiFormLogic.ocoCheckbox.label}
                    logicImageSrc={infoImagesHelper[multiFormLogic.ocoCheckbox.descriptionImage]}
                  />
                </div>
                <Switch
                  checked={multiFormLogic.ocoCheckbox.get}
                  onChange={multiFormLogic.ocoCheckbox.set}
                  disabled={multiFormLogic.ocoCheckbox.isDisabled || !allowOrderEdit}
                />
              </div>
              <div className={styles.item}>
                <div className={styles.label}>
                  {multiFormLogic.stopLossCheckbox.label + pipsLabelWithParentheses}
                  <HelpHalfModal
                    className={styles.help}
                    content={multiFormLogic.stopLossCheckbox.tooltipMessage}
                    logicName={multiFormLogic.stopLossCheckbox.label}
                    logicImageSrc={infoImagesHelper[multiFormLogic.stopLossCheckbox.descriptionImage]}
                  />
                </div>
                <Switch
                  checked={multiFormLogic.stopLossCheckbox.get}
                  onChange={multiFormLogic.stopLossCheckbox.set}
                  disabled={multiFormLogic.stopLossCheckbox.isDisabled || !allowOrderEdit}
                />
              </div>
              {multiFormLogic.stopLossCheckbox.get && (
                <div className={classNames(styles.item, styles.right)}>
                  <InputNumber
                    value={multiFormLogic.stopLossInput.get}
                    onChange={multiFormLogic.stopLossInput.set}
                    disabled={multiFormLogic.stopLossInput.isDisabled || !allowOrderEdit}
                    step={multiFormLogic.stopLossInput.step}
                    min={multiFormLogic.stopLossInput.min}
                    name={multiFormLogic.stopLossInput.name}
                    errorMessages={multiFormLogic.errorMessages}
                    withErrorTooltip
                    validateNegativeValues
                  />
                </div>
              )}
              <div
                className={classNames(
                  styles.counterSetting,
                  multiFormLogic.invalidCounter.condition ? styles.itemBorderBottomNone : null,
                )}
              >
                <div className={styles.counterItem}>
                  <div className={styles.label}>
                    {multiFormLogic.counterCheckbox.label}
                    <HelpHalfModal
                      className={styles.help}
                      content={multiFormLogic.counterCheckbox.tooltipMessage}
                      logicName={multiFormLogic.counterCheckbox.label}
                      logicImageSrc={infoImagesHelper[multiFormLogic.counterCheckbox.descriptionImage]}
                    />
                  </div>
                  <Switch
                    checked={multiFormLogic.counterCheckbox.get}
                    onChange={multiFormLogic.counterCheckbox.set}
                    disabled={multiFormLogic.counterCheckbox.isDisabled || !allowOrderEdit}
                  />
                </div>
                {multiFormLogic.counterCheckbox.get && (
                  <div className={classNames(styles.counterItem, styles.counterRight)}>
                    <div className={styles.label}>
                      {multiFormLogic.counterInput.label + pipsLabelWithParentheses}
                      <HelpHalfModal
                        className={styles.help}
                        content={multiFormLogic.counterInput.tooltipMessage}
                        logicName={multiFormLogic.counterInput.label}
                        logicImageSrc={infoImagesHelper[multiFormLogic.counterInput.descriptionImage]}
                      />
                    </div>
                    <InputNumber
                      width={shortNumberInputWidth}
                      value={multiFormLogic.counterInput.get}
                      onChange={multiFormLogic.counterInput.set}
                      disabled={multiFormLogic.counterInput.isDisabled || !allowOrderEdit}
                      step={multiFormLogic.counterInput.step}
                      min={multiFormLogic.counterInput.min}
                      name={multiFormLogic.counterInput.name}
                      errorMessages={multiFormLogic.errorMessages}
                      validateNegativeValues
                      withErrorTooltip
                    />
                  </div>
                )}
                {multiFormLogic.counterCheckbox.get && (
                  <div className={classNames(styles.counterItem, styles.counterRight)}>
                    <div className={styles.label}>
                      {multiFormLogic.counterFixedCheckbox.label}
                      <HelpHalfModal
                        className={styles.help}
                        content={multiFormLogic.counterFixedCheckbox.tooltipMessage}
                        logicName={multiFormLogic.counterFixedCheckbox.label}
                        logicImageSrc={infoImagesHelper[multiFormLogic.counterFixedCheckbox.descriptionImage]}
                      />
                    </div>
                    <Switch
                      checked={multiFormLogic.counterFixedCheckbox.get}
                      onChange={multiFormLogic.counterFixedCheckbox.set}
                      disabled={multiFormLogic.counterFixedCheckbox.isDisabled || !allowOrderEdit}
                    />
                  </div>
                )}
              </div>
              {multiFormLogic.invalidCounter.condition && (
                <div className={classNames(styles.item)}>
                  <div className={classNames(styles.counterItem, styles.validationErrorMessage)}>
                    {multiFormLogic.invalidCounter.message}
                  </div>
                </div>
              )}
              <div
                className={classNames(
                  styles.counterSetting,
                  multiFormLogic.invalidCounter.condition ? styles.itemBorderBottomNone : null,
                )}
              >
                <div className={styles.counterItem}>
                  <div className={styles.label}>
                    {multiFormLogic.followCheckbox.label}
                    <HelpHalfModal
                      className={styles.help}
                      content={multiFormLogic.followCheckbox.tooltipMessage}
                      logicName={multiFormLogic.followCheckbox.label}
                      logicImageSrc={infoImagesHelper[multiFormLogic.followCheckbox.descriptionImage]}
                    />
                  </div>
                  <Switch
                    checked={multiFormLogic.followCheckbox.get}
                    onChange={multiFormLogic.followCheckbox.set}
                    disabled={multiFormLogic.followCheckbox.isDisabled || !allowOrderEdit}
                  />
                </div>
                {multiFormLogic.followCheckbox.get && (
                  <div className={classNames(styles.counterItem, styles.counterRight)}>
                    <div className={styles.label}>
                      {multiFormLogic.followInput.label + pipsLabelWithParentheses}
                      <HelpHalfModal
                        className={styles.help}
                        content={multiFormLogic.followInput.tooltipMessage}
                        logicName={multiFormLogic.followInput.label}
                        logicImageSrc={infoImagesHelper[multiFormLogic.followInput.descriptionImage]}
                      />
                    </div>
                    <InputNumber
                      width={shortNumberInputWidth}
                      value={multiFormLogic.followInput.get}
                      onChange={multiFormLogic.followInput.set}
                      disabled={multiFormLogic.followInput.isDisabled || !allowOrderEdit}
                      min={multiFormLogic.followInput.min}
                      name={multiFormLogic.followInput.name}
                      errorMessages={multiFormLogic.errorMessages}
                      step={multiFormLogic.followInput.step}
                      validateNegativeValues
                      withErrorTooltip
                    />
                  </div>
                )}
              </div>
              {multiFormLogic.invalidCounter.condition && (
                <div className={classNames(styles.item, styles.itemPaddingBottom)}>
                  <div className={classNames(styles.counterItem, styles.validationErrorMessage)}>
                    {multiFormLogic.invalidCounter.message}
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : null}

        {allowOrderEdit && (
          <div className={classNames(styles.buttonContainer)}>
            <Button
              onClick={multiFormLogic.submit.handler}
              disabled={multiFormLogic.submit.isDisabled}
              width={200}
              outlined
            >
              シミュレーションを見る
            </Button>
          </div>
        )}
      </div>
    </div>
  );
});

SideMenuOptionsTradeAP.displayName = 'SideMenuOptionsTradeAP';

export default memo(SideMenuOptionsTradeAP);
